import React from "react"
import { Link } from "gatsby"
import { ALL_INTEGRATIONS } from "./constants"
import { IntegrationPlatform } from "./types"
import * as buttonStyles from "../../ds/button.module.css"
import * as gradientStyles from "../../ds/gradients.module.css"
import PremagicIcon from "../../images/logo/144x144.png"

export const IntegrationListingHero: React.FC = () => {
  const badges = [
    "1-Click Setup",
    "2-Way Data Sync",
    "Real-Time Attendee Import",
    "AI-Powered Photo Distribution",
  ]

  return (
    <section
      className={`relative overflow-hidden py-16 md:py-24 ${gradientStyles["gradient-light-pink"]}`}
    >
      <div className="pm-container relative z-10 text-center">
        <h1 className="mb-6 text-4xl font-bold leading-tight text-gray-900 md:text-6xl">
          Connect Your{" "}
          <span className={gradientStyles["gradient-text"]}>Event Stack</span>
        </h1>
        <p className="mx-auto mb-10 max-w-2xl text-lg text-gray-600 md:text-xl">
          Seamless integrations with the platforms you already use
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          {badges.map((badge, index) => (
            <span
              key={index}
              className="rounded-full bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-gray-200"
            >
              {badge}
            </span>
          ))}
        </div>
      </div>

      <div className="absolute -left-20 top-20 h-64 w-64 rounded-full bg-pink-200 opacity-20 blur-3xl"></div>
      <div className="absolute -right-20 bottom-20 h-64 w-64 rounded-full bg-blue-200 opacity-20 blur-3xl"></div>
    </section>
  )
}

export const HowIntegrationsWork: React.FC = () => {
  const steps = [
    {
      number: 1,
      title: "Connect",
      description:
        "Link your favorite tools with a few clicks using our secure OAuth integration.",
    },
    {
      number: 2,
      title: "Sync & Personalize",
      description:
        "Automatically import attendee data and generate personalized assets instantly.",
    },
    {
      number: 3,
      title: "Amplify & Track",
      description:
        "Distribute content via AI and track engagement metrics in real-time.",
    },
  ]

  return (
    <section className="bg-white py-12 md:py-20">
      <div className="pm-container">
        <div className="mb-12 text-center">
          <h2 className="text-3xl font-bold text-gray-900 md:text-4xl">
            How It Works
          </h2>
        </div>
        <div className="grid gap-8 md:grid-cols-3">
          {steps.map(step => (
            <div
              key={step.number}
              className="flex flex-col items-center text-center"
            >
              <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[#1F0F61] text-2xl font-bold text-white shadow-lg">
                {step.number}
              </div>
              <h3 className="mb-3 text-xl font-bold text-gray-900">
                {step.title}
              </h3>
              <p className="text-gray-600">{step.description}</p>
            </div>
          ))}
        </div>

      </div>
    </section>
  )
}

const IntegrationCard: React.FC<{ integration: IntegrationPlatform }> = ({
  integration,
}) => {
  const isActive = !integration.isComingSoon

  const CardContent = () => (
    <div className="flex h-full flex-col rounded-2xl border border-gray-100 bg-white p-6 shadow-sm transition-all hover:shadow-md">
      <div className="mb-6 flex items-center gap-4">
        <img
          src={integration.logoUrl}
          alt={`${integration.name} logo`}
          className="h-10 w-10 object-contain"
        />
        <span className="text-gray-300 text-xl font-light">+</span>
        <img
          src={PremagicIcon}
          alt="Premagic logo"
          className="h-10 w-10 object-contain"
        />
      </div>

      <h3 className="mb-2 text-xl font-bold text-gray-900">
        {integration.name}
      </h3>
      <p className="mb-4 flex-grow text-sm text-gray-600 line-clamp-3">
        {integration.shortDescription}
      </p>

      <div className="mb-6 flex flex-wrap gap-2">
        {integration.tags.slice(0, 2).map((tag, idx) => (
          <span
            key={idx}
            className="rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600"
          >
            {tag}
          </span>
        ))}
      </div>

      <div className="mt-auto">
        {isActive ? (
          <span className="text-sm font-semibold text-[#2b4bc3] group-hover:underline">
            Learn more →
          </span>
        ) : (
          <span className="inline-block rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-500">
            {integration.comingSoonLabel || "Coming Soon"}
          </span>
        )}
      </div>
    </div>
  )

  if (isActive) {
    return (
      <Link
        to={`/integrations/${integration.slug}/`}
        className="group block h-full"
      >
        <CardContent />
      </Link>
    )
  }

  return (
    <div className="block h-full opacity-75">
      <CardContent />
    </div>
  )
}

export const IntegrationCardsGrid: React.FC = () => {
  return (
    <section className="bg-gray-50 py-12 md:py-20">
      <div className="pm-container">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {ALL_INTEGRATIONS.map(integration => (
            <IntegrationCard key={integration.slug} integration={integration} />
          ))}
        </div>
      </div>
    </section>
  )
}

export const TrustedPartners: React.FC = () => {
  return (
    <section className="border-t border-gray-100 bg-white py-10 md:py-12">
      <div className="pm-container">
        <p className="mb-10 text-center text-sm font-semibold uppercase tracking-wider text-gray-500">
          Trusted by leading platforms
        </p>
        <div className="flex flex-wrap justify-center gap-8 md:gap-12">
          {ALL_INTEGRATIONS.map(integration => (
            <div
              key={integration.slug}
              className="flex flex-col items-center gap-2 opacity-60 transition-opacity hover:opacity-100"
            >
              <div className="h-10 w-32 flex items-center justify-center">
                <img
                  src={integration.logoUrl}
                  alt={integration.name}
                  className="max-h-full max-w-full object-contain grayscale hover:grayscale-0 transition-all"
                />
              </div>
              <span className="text-xs text-gray-400">{integration.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export const IntegrationCTA: React.FC = () => {
  return (
    <section className={`py-16 md:py-20 ${gradientStyles["gradient-pink-bottom-top"]}`}>
      <div className="pm-container text-center">
        <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
          Don't See Your Platform?
        </h2>
        <p className="mx-auto mb-6 max-w-2xl text-lg text-gray-600">
          We're constantly adding new integrations. CSV import works with any
          platform today, and our team can usually scope a new direct
          integration quickly.
        </p>
        <p className="mx-auto mb-10 max-w-2xl text-sm text-gray-500">
          Not sure if CSV will fit your workflow?{" "}
          <Link
            to="/integrations/how-it-works/#data-integration"
            className="font-semibold text-[#2b4bc3] hover:underline"
          >
            See how data flows in and out of Premagic
          </Link>
          .
        </p>
        <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
          <Link
            to="/request-a-demo/"
            className={`${buttonStyles.btn} ${buttonStyles["btn-primary"]}`}
          >
            Request an Integration
          </Link>
          <Link
            to="/request-a-demo/"
            className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
          >
            Get a Demo
          </Link>
        </div>
      </div>
    </section>
  )
}

const IntegrationListing: React.FC = () => {
  return (
    <div className="integration-listing">
      <IntegrationListingHero />
      <HowIntegrationsWork />
      <IntegrationCardsGrid />
      <TrustedPartners />
      <IntegrationCTA />
    </div>
  )
}

export default IntegrationListing
